/** Exact, bounded-at-commit arithmetic for TCP-SP-2.0. BigInt never enters JSON. */
export interface SerializedRational { numerator: string; denominator: string }
export interface FixedPoint { basisPoints: number; residual: number }
export const RESIDUAL_SCALE = 1_000_000n;
const gcd = (a: bigint, b: bigint): bigint => {
  a = a < 0n ? -a : a; b = b < 0n ? -b : b;
  while (b !== 0n) { const next = a % b; a = b; b = next; }
  return a;
};

export class Rational {
  readonly numerator: bigint;
  readonly denominator: bigint;
  constructor(numerator: bigint, denominator = 1n) {
    if (denominator === 0n) throw new Error("Exact arithmetic division by zero");
    if (denominator < 0n) { numerator = -numerator; denominator = -denominator; }
    const divisor = gcd(numerator, denominator);
    this.numerator = numerator / divisor;
    this.denominator = denominator / divisor;
  }
  add(value: RationalInput): Rational { const v = rational(value); return new Rational(this.numerator * v.denominator + v.numerator * this.denominator, this.denominator * v.denominator); }
  sub(value: RationalInput): Rational { const v = rational(value); return new Rational(this.numerator * v.denominator - v.numerator * this.denominator, this.denominator * v.denominator); }
  mul(value: RationalInput): Rational { const v = rational(value); return new Rational(this.numerator * v.numerator, this.denominator * v.denominator); }
  div(value: RationalInput): Rational { const v = rational(value); return new Rational(this.numerator * v.denominator, this.denominator * v.numerator); }
  compare(value: RationalInput): number { const v = rational(value); const difference = this.numerator * v.denominator - v.numerator * this.denominator; return difference < 0n ? -1 : difference > 0n ? 1 : 0; }
  min(value: RationalInput): Rational { const v = rational(value); return this.compare(v) <= 0 ? this : v; }
  max(value: RationalInput): Rational { const v = rational(value); return this.compare(v) >= 0 ? this : v; }
  clamp(low: RationalInput = 0, high: RationalInput = 100): Rational { return this.max(low).min(high); }
  truncScaled(scale: bigint): bigint { return this.numerator * scale / this.denominator; }
  ceil(): bigint { const quotient = this.numerator / this.denominator; return quotient + (this.numerator > 0n && this.numerator % this.denominator !== 0n ? 1n : 0n); }
  serialize(): SerializedRational { return { numerator: this.numerator.toString(), denominator: this.denominator.toString() }; }
  /** Only use at display boundaries or for exact terminating-decimal mirrors. */
  toNumber(): number { return Number(this.numerator) / Number(this.denominator); }
}

export type RationalInput = Rational | number | string | bigint | SerializedRational;
export function rational(value: RationalInput): Rational {
  if (value instanceof Rational) return value;
  if (typeof value === "bigint") return new Rational(value);
  if (typeof value === "object") return new Rational(BigInt(value.numerator), BigInt(value.denominator));
  if (typeof value === "number" && !Number.isFinite(value)) throw new Error("Non-finite exact arithmetic input");
  const match = /^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(String(value));
  if (!match || !(match[2] || match[3])) throw new Error("Invalid rational decimal");
  const decimalPlaces = (match[3]?.length ?? 0) - Number(match[4] ?? 0);
  const numerator = BigInt(`${match[1] === "-" ? "-" : ""}${match[2] || "0"}${match[3] || ""}`);
  return decimalPlaces >= 0 ? new Rational(numerator, 10n ** BigInt(decimalPlaces)) : new Rational(numerator * 10n ** BigInt(-decimalPlaces));
}

export function safeInteger(value: bigint): number {
  const result = Number(value);
  if (!Number.isSafeInteger(result) || BigInt(result) !== value) throw new Error("Exact arithmetic integer exceeds safe persisted range");
  return result;
}

/** One and only one truncation of a full delta, then carry whole basis points. */
export function commitDelta(field: FixedPoint, delta: RationalInput, low = 0, high = 100): FixedPoint {
  const accumulated = BigInt(field.residual) + rational(delta).truncScaled(100n * RESIDUAL_SCALE);
  let basisPoints = BigInt(field.basisPoints) + accumulated / RESIDUAL_SCALE;
  let residual = accumulated % RESIDUAL_SCALE;
  const minimum = rational(low).truncScaled(100n), maximum = rational(high).truncScaled(100n);
  if (basisPoints < minimum) { basisPoints = minimum; residual = 0n; }
  if (basisPoints > maximum) { basisPoints = maximum; residual = 0n; }
  if ((basisPoints === minimum && residual < 0n) || (basisPoints === maximum && residual > 0n)) residual = 0n;
  return { basisPoints: safeInteger(basisPoints), residual: safeInteger(residual) };
}

/** Algebraic assignments have no prior carry; retain their truncated remainder. */
export function assignFixed(value: RationalInput, low = 0, high = 100): FixedPoint {
  return commitDelta({ basisPoints: 0, residual: 0 }, rational(value).clamp(low, high), low, high);
}

/** Resource units are committed in hundredths; ratios then remain exact. */
export function hundredths(value: RationalInput): number { return safeInteger(rational(value).truncScaled(100n)) / 100; }
